import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { useState } from "react";
import { galText } from "./accounts";
import * as api from "./api";
import { useTitle } from "./hooks";
import { SERVER_PROBLEM } from "./save";
import { SALES_PEOPLE_COLUMNS, SALES_STATE_COLUMNS, SALES_WEEK_COLUMNS, WEEK_COLUMNS } from "./columns";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { scorecardKey } from "./store/pageViews";
import { loadFailure, useView, ViewFreshness } from "./store/useView";
import { Hint } from "./Hint";
import { dayText } from "./accounts";
import {
  collectorText,
  completeWeeks,
  countRows,
  hoursText,
  lostText,
  personText,
  shareText,
  stateText,
  latestComplete,
  type Metric,
  METRICS,
  accuracyHelp,
  gallonsText,
  accuracyValue,
  metricNote,
  metricText,
  metricValue,
  readWeeks,
  spark,
  WEEK_CHOICES,
  weekLabel,
  writeWeeks,
} from "./scorecard";
import { Skeleton, SkeletonBar } from "./Skeleton";

type CardLoad = api.Scorecard | "session" | "server" | "network" | undefined;

const SPARK_WIDTH = 240;
const SPARK_HEIGHT = 48;

function MetricCard({ metric, weeks }: { metric: Metric; weeks: api.ScorecardWeek[] }) {
  const latest = latestComplete(weeks);
  const shown = completeWeeks(weeks);
  const line = spark(
    shown.map((week) => metricValue(metric.key, week)),
    SPARK_WIDTH,
    SPARK_HEIGHT,
  );
  return (
    <section className="score-card" aria-labelledby={`score-${metric.key}`} data-testid={`score-${metric.key}`}>
      <h2 id={`score-${metric.key}`}>{metric.label}</h2>
      <p className="score-value">
        {!latest ? (
          "No complete week"
        ) : metric.key === "accuracy" ? (
          <Hint text={accuracyHelp(latest, galText)}>{metricText(metric.key, latest)}</Hint>
        ) : (
          metricText(metric.key, latest)
        )}
        {latest && <span className="sub">Week of {weekLabel(latest)}</span>}
      </p>
      <svg
        className="score-spark"
        viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
        role="img"
        aria-label={`${metric.label} for each complete week, the oldest first: ${shown
          .map((week) => metricText(metric.key, week))
          .join(", ")}`}
      >
        <line x1="0" y1={SPARK_HEIGHT - 3} x2={SPARK_WIDTH} y2={SPARK_HEIGHT - 3} className="score-base" />
        {line.path && <path d={line.path} className="score-line" vectorEffect="non-scaling-stroke" />}
        {line.last && <circle cx={line.last.x} cy={line.last.y} r="3" className="score-dot" />}
      </svg>
      <p className="hint">{metricNote(metric, weeks)}</p>
    </section>
  );
}

function SalesNumbers({ row }: { row: api.SalesRow }) {
  return (
    <>
      <td className="num" data-label="New accounts">{row.wins}</td>
      <td className="num" data-label="Renewals">{row.renewals}</td>
    </>
  );
}

function SalesShares({ row }: { row: api.SalesRow }) {
  return (
    <>
      <td className="num" data-label="All accounts">{row.accounts}</td>
      <td className="num" data-label="Commercial active %">{shareText(row.active)}</td>
      <td className="num" data-label="Regular %">{shareText(row.regular)}</td>
      <td className="num" data-label="On call %">{shareText(row.on_call)}</td>
      <td className="num" data-label="Signed regular %">{shareText(row.signed_regular)}</td>
    </>
  );
}

function CountList({ title, rows, testId }: { title: string; rows: [string, number][]; testId: string }) {
  return (
    <section className="score-card" aria-label={title} data-testid={testId}>
      <h3>{title}</h3>
      {rows.length === 0 ? (
        <p className="sub">None in these weeks.</p>
      ) : (
        <ul className="count-list">
          {rows.map(([label, n]) => (
            <li key={label}>
              <span>{label}</span> <span className="num">{n}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SalesSection({ sales }: { sales: api.Sales }) {
  const [weekSort, setWeekSort] = useSort("sales-week-sort", SALES_WEEK_COLUMNS);
  const [peopleSort, setPeopleSort] = useSort("sales-sort", SALES_PEOPLE_COLUMNS);
  const [stateSort, setStateSort] = useSort("state-sort", SALES_STATE_COLUMNS);
  return (
    <section aria-labelledby="score-sales" data-testid="score-sales">
      <h2 id="score-sales">Sales</h2>
      <p className="hint">
        From the deals, contracts, leads and notes. The account columns count today; the other columns count these weeks. All accounts counts each account in the book, also the accounts with service off or on hold. Thus it can be more than the number at the top. Commercial active % uses the commercial status, not the service status.
      </p>
      <h3>Each salesperson</h3>
      <SortSelect id="sales-sort" columns={SALES_PEOPLE_COLUMNS} sort={peopleSort} onSort={setPeopleSort} fallback="Salesperson" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="The sales numbers of each salesperson">
        <table className="accounts scorecard list" data-testid="sales-people" data-cols="sales-sort">
          <thead>
            <tr>
              {SALES_PEOPLE_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={peopleSort} onSort={setPeopleSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortRows(sales.people, SALES_PEOPLE_COLUMNS, peopleSort).map((row) => (
              <tr key={row.key || "house"} data-testid="sales-person">
                <th scope="row" title={row.name || undefined}>
                  <span className="clip name" title={personText(row)}>{personText(row)}</span>
                </th>
                <SalesNumbers row={row} />
                <td className="num" data-label="Lost leads">{row.losses}</td>
                <td className="num" data-label="Hours per sign-up">
                  <Hint text={`${row.minutes} minutes in notes.`} stop={false}>
                    {hoursText(row)}
                  </Hint>
                </td>
                <SalesShares row={row} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3>Each state</h3>
      <SortSelect id="state-sort" columns={SALES_STATE_COLUMNS} sort={stateSort} onSort={setStateSort} fallback="State" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="The sales numbers of each state">
        <table className="accounts scorecard list" data-testid="sales-states" data-cols="state-sort">
          <thead>
            <tr>
              {SALES_STATE_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={stateSort} onSort={setStateSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortRows(sales.states, SALES_STATE_COLUMNS, stateSort).map((row) => (
              <tr key={row.key || "none"} data-testid="sales-state">
                <th scope="row">{stateText(row)}</th>
                <SalesNumbers row={row} />
                <SalesShares row={row} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <h3>Each week</h3>
      <SortSelect id="sales-week-sort" columns={SALES_WEEK_COLUMNS} sort={weekSort} onSort={setWeekSort} fallback="Newest week first" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="The sales numbers of each week">
        <table className="accounts scorecard list" data-testid="sales-weeks" data-cols="sales-week-sort">
          <thead>
            <tr>
              {SALES_WEEK_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={weekSort} onSort={setWeekSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortRows([...sales.weeks].reverse(), SALES_WEEK_COLUMNS, weekSort).map((week) => (
              <tr key={week.week_start} data-testid="sales-week">
                <th scope="row">{dayText(week.week_start)}</th>
                <td className="num" data-label="New accounts">{week.wins}</td>
                <td className="num" data-label="Renewals">{week.renewals}</td>
                <td className="num" data-label="Lost leads">{week.losses}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="score-grid">
        <CountList title="Why leads were lost" rows={countRows(sales.lost_reasons, lostText)} testId="sales-lost" />
        <CountList title="New accounts by previous collector" rows={countRows(sales.collectors, collectorText)} testId="sales-collectors" />
      </div>
    </section>
  );
}

export function ScorecardPage({
  org,
  auth,
  user,
  canView,
}: {
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const [count, setCountState] = useState(() => readWeeks(location.search));
  const setCount = (next: number) => {
    setCountState(next);
    history.replaceState(history.state, "", `${location.pathname}${writeWeeks(location.search, next)}${location.hash}`);
  };
  const view = useView(canView === true ? scorecardKey(count) : null, async () => api.scorecard(await user.getIdToken(), count));
  const found: CardLoad = view.data ?? (view.error ? loadFailure(view.error) : undefined);
  const [sort, setSort] = useSort("sort", WEEK_COLUMNS);
  useTitle(`Scorecard · ${org.name}`);
  if (canView === false) {
    return (
      <main data-testid="scorecard-hidden">
        <h1 tabIndex={-1}>Scorecard</h1>
        <p>Your role does not show the scorecard.</p>
      </main>
    );
  }
  return (
    <main className="issues-page scorecard-page wide" data-testid="scorecard-page">
      <h1 tabIndex={-1}>Scorecard</h1>
      <div className="tabs" role="group" aria-label="Weeks to show">
        {WEEK_CHOICES.map((n) => (
          <button key={n} type="button" aria-pressed={count === n} data-testid={`score-weeks-${n}`} onClick={() => setCount(n)}>
            {n} weeks
          </button>
        ))}
      </div>
      <ViewFreshness view={view} timeZone={org.time_zone} auth={auth} />
      {found === undefined ? (
        <>
          {view.at === null && (
            <p className="sub">
              <SkeletonBar width="9rem" />
            </p>
          )}
          <p className="sub skeleton-note">
            <SkeletonBar width="70%" />
          </p>
          <Skeleton shape="cards" rows={4} />
        </>
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={view.reload}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <p className="sub" data-testid="score-accounts" data-accounts={found.accounts}>
            {found.accounts} accounts in service (regular, new or on call). This week is not complete, so the numbers at the top are for the last complete
            week.
          </p>
          <div className="score-grid">
            {METRICS.map((metric) => (
              <MetricCard key={metric.key} metric={metric} weeks={found.weeks} />
            ))}
          </div>
          <section aria-labelledby="score-weeks">
            <h2 id="score-weeks">Each week</h2>
            <SortSelect id="score-sort" columns={WEEK_COLUMNS} sort={sort} onSort={setSort} fallback="Newest week first" />
            <div className="table-scroll" tabIndex={0} role="region" aria-label="The numbers of each week">
              <table className="accounts scorecard list" data-testid="score-table" data-cols="score-sort">
                <thead>
                  <tr>
                    {WEEK_COLUMNS.map((column) => (
                      <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortRows([...found.weeks].reverse(), WEEK_COLUMNS, sort).map((week) => (
                    <tr key={week.week_start} data-testid="score-week" data-partial={week.partial}>
                      <th scope="row" title={`Rotation week ${week.rotation_week}`}>
                        {weekLabel(week)}
                        <span className="sub desk-hide">Rotation week {week.rotation_week}</span>
                        {week.partial && <span className="sub"> (not complete)</span>}
                      </th>
                      <td className="num" data-label="Calls at fault">{week.calls_at_fault}</td>
                      <td className="num" data-label="Overflows / 100">{metricText("overflows_per_100", week)}</td>
                      <td className="num" data-label="Accuracy" data-testid="score-accuracy">
                        <Hint text={accuracyHelp(week, galText)}>{metricText("accuracy", week)}</Hint>
                        {accuracyValue(week) !== null && (
                          <span className="sub desk-hide">{gallonsText(week, galText)}</span>
                        )}
                      </td>
                      <td className="num" data-label="Stops done">
                        {week.done_stops} of {week.planned_stops}
                      </td>
                      <td className="num" data-label="Extra stops">{week.extra_stops}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          {found.sales && <SalesSection sales={found.sales} />}
        </>
      )}
    </main>
  );
}
